import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, Check, Circle } from 'lucide-react';
import {
  BrandAnchor,
  FieldRow,
  ForgotPasswordCard,
  ForgotPasswordLink,
  FormSection,
  Heading,
  LoginCard,
  LoginContainer,
  PasswordHeader,
  PasswordInputWrapper,
  PasswordLabel,
  PasswordToggleButton,
  PolicyChip,
  PolicyChips,
  RequiredMark,
  SignInButton,
  SignUpBrandAnchor,
  SignUpCard,
  SignUpContainer,
  SignUpFooterText,
  SignUpForm,
  SignUpText,
} from './Login.styles';
import { Input } from '../../../components/uielements/input';
import { PageWrapper } from '../../GeneralPages/landingPages/components/shared.styles';
import {
  registration,
  resetPassword,
  forgotPassword,
  resendVerificationCode,
  confirmUser,
} from '../../../services/auth';
import { useAuth } from '../../../hooks/useAuth';
import { validateEmail, validatePassword } from '../../../helper/validationHelper';
import { checkPasswordPolicy, isPasswordPolicyMet } from '../../../helper/passwordpolicyHelper';
import { Navbar } from '../Navbar';
import { ThemeProvider } from 'styled-components';
import { darkTheme, lightTheme } from '../../../settings/appStyle/theme';

// ─── helpers ────────────────────────────────────────────────────────────────

const HelperText = ({ children }) => (
  <span
    role="alert"
    style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}
  >
    {children}
  </span>
);

const PasswordVisibilityToggle = ({ visible, onToggle }) => (
  <PasswordToggleButton
    type="button"
    onClick={onToggle}
    aria-label={visible ? 'Hide password' : 'Show password'}
    tabIndex={0}
  >
    {visible ? <EyeOff size={17} /> : <Eye size={17} />}
  </PasswordToggleButton>
);

// Label text + red asterisk for required fields
export const RequiredLabel = ({ children }) => (
  <>
    {children}
    <RequiredMark aria-hidden="true">*</RequiredMark>
  </>
);

// ─── Root router ────────────────────────────────────────────────────────────

const Login = () => {
  const [formState, setFormState] = useState('sign-in'); // 'sign-in' | 'sign-up' | 'forgot-password'

  const [isDark, setIsDark] = useState(() => {
    const savedTheme = localStorage.getItem('theme');

    if (savedTheme) {
      return savedTheme === 'dark';
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  const toggleTheme = () => {
    setIsDark((prev) => !prev);
  };

  return (
    <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
      <>
        <Navbar isDark={isDark} toggleTheme={toggleTheme} />

        {formState === 'sign-in' && (
          <SignIn
            onGoSignUp={() => setFormState('sign-up')}
            onGoForgotPassword={() => setFormState('forgot-password')}
          />
        )}

        {formState === 'sign-up' && <SignUp onGoSignIn={() => setFormState('sign-in')} />}

        {formState === 'forgot-password' && (
          <ForgotPassword onGoSignIn={() => setFormState('sign-in')} />
        )}
      </>{' '}
    </ThemeProvider>
  );
};

export default Login;

// ─── Sign In ────────────────────────────────────────────────────────────────

const SignIn = ({ onGoSignUp, onGoForgotPassword }) => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);

  const validate = () => {
    const next = { email: '', password: '' };
    if (!validateEmail(email)) next.email = 'Please enter a valid email address.';
    if (!validatePassword(password)) next.password = 'Please enter your password.';
    setErrors(next);
    return !next.email && !next.password;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setIsLoading(true);
      await login(email, password);
    } catch (error) {
      setIsLoading(false);
      if (error?.response?.data?.message === 'User Not Confirmed!') {
        alert('Please verify your email address to continue.');
        setPendingVerification(true);
      } else if (error?.response?.data?.detail === 'User not verified. Please verify your email.') {
        alert('Please verify your email address to continue.');
        setPendingVerification(true);
      } else {
        alert('Credentials not found. Please check your email and password.');
      }
    }
  };

  if (pendingVerification) {
    return (
      <EmailVerification
        email={email}
        onVerificationSuccess={() => setPendingVerification(false)}
      />
    );
  }

  return (
    <PageWrapper>
      <LoginContainer>
        <LoginCard>
          <BrandAnchor>
            <Heading>Sign In</Heading>
          </BrandAnchor>

          <FormSection onSubmit={handleSubmit}>
            <div style={{ width: '100%' }}>
              <Input
                label="Email Address"
                type="email"
                name="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors((p) => ({ ...p, email: '' }));
                }}
                iconSize="16.667px"
              />
              {errors.email && <HelperText>{errors.email}</HelperText>}
            </div>

            <div style={{ width: '100%' }}>
              <PasswordHeader>
                <PasswordLabel htmlFor="password">Password</PasswordLabel>
                <ForgotPasswordLink
                  as="span"
                  role="button"
                  tabIndex={0}
                  onClick={onGoForgotPassword}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onGoForgotPassword()}
                  style={{ cursor: 'pointer' }}
                >
                  Forgot password?
                </ForgotPasswordLink>
              </PasswordHeader>
              <PasswordInputWrapper>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  id="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setErrors((p) => ({ ...p, password: '' }));
                  }}
                  iconSize="13.333px"
                />
                <PasswordVisibilityToggle
                  visible={showPassword}
                  onToggle={() => setShowPassword((v) => !v)}
                />
              </PasswordInputWrapper>
              {errors.password && <HelperText>{errors.password}</HelperText>}
            </div>

            <SignInButton type="submit" disabled={isLoading}>
              {isLoading ? 'Signing in…' : 'Sign In'}
            </SignInButton>
          </FormSection>

          <SignUpText>
            New to Urban Compass?{' '}
            <span
              role="button"
              tabIndex={0}
              onClick={onGoSignUp}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onGoSignUp()}
              style={{ cursor: 'pointer' }}
            >
              Create an account
            </span>
          </SignUpText>
        </LoginCard>
      </LoginContainer>
    </PageWrapper>
  );
};

// ─── Sign Up ────────────────────────────────────────────────────────────────

const SignUp = ({ onGoSignIn }) => {
  const [fields, setFields] = useState({
    clientCode: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);

  const policyResults = checkPasswordPolicy(fields.password);
  const policyMet = isPasswordPolicyMet(fields.password);
  const passwordsMatch = fields.password === fields.confirmPassword;
  const showMismatch = fields.confirmPassword.length > 0 && !passwordsMatch;
  const canSubmit = policyMet && fields.confirmPassword !== '' && passwordsMatch;

  const set = (key) => (e) => {
    setFields((p) => ({ ...p, [key]: e.target.value }));
    setErrors((p) => ({ ...p, [key]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!fields.clientCode.trim()) next.clientCode = 'Client code is required.';
    if (!fields.username.trim()) next.username = 'Name is required.';
    if (!validateEmail(fields.email)) next.email = 'Please enter a valid email address.';
    if (!fields.password) next.password = 'Password is required.';
    else if (!policyMet) next.password = 'Password does not meet the policy.';
    if (!fields.confirmPassword) next.confirmPassword = 'Confirm password is required.';
    else if (!passwordsMatch) next.confirmPassword = 'Passwords do not match.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setIsLoading(true);
      await registration({
        clientCode: fields.clientCode,
        username: fields.username,
        email: fields.email,
        password: fields.password,
        userType: 'USER',
      });
      setPendingVerification(true);
    } catch (error) {
      alert(error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (pendingVerification) {
    return <EmailVerification email={fields.email} onVerificationSuccess={onGoSignIn} />;
  }

  return (
    <PageWrapper>
      <SignUpContainer>
        <SignUpCard>
          <SignUpBrandAnchor>
            <Heading>Create Account</Heading>
          </SignUpBrandAnchor>

          <SignUpForm onSubmit={handleSubmit}>
            {/* Row 1: Council code + Name */}
            <FieldRow>
              <div style={{ minWidth: 0 }}>
                <Input
                  label={<RequiredLabel>Client Code</RequiredLabel>}
                  type="text"
                  name="clientCode"
                  placeholder="Enter the client code"
                  value={fields.clientCode}
                  onChange={set('clientCode')}
                  iconSize="16.667px"
                />
                {errors.clientCode && <HelperText>{errors.clientCode}</HelperText>}
              </div>

              <div style={{ minWidth: 0 }}>
                <Input
                  label={<RequiredLabel>Name</RequiredLabel>}
                  type="text"
                  name="username"
                  placeholder="Your full name"
                  value={fields.username}
                  onChange={set('username')}
                  iconSize="16.667px"
                />
                {errors.username && <HelperText>{errors.username}</HelperText>}
              </div>
            </FieldRow>

            {/* Row 2: Email */}
            <div style={{ width: '100%' }}>
              <Input
                label={<RequiredLabel>Email Address</RequiredLabel>}
                type="email"
                name="email"
                placeholder="name@company.com"
                value={fields.email}
                onChange={set('email')}
                iconSize="16.667px"
              />
              {errors.email && <HelperText>{errors.email}</HelperText>}
            </div>

            {/* Row 3: Password + Confirm password */}
            <FieldRow>
              <div style={{ minWidth: 0 }}>
                <PasswordHeader>
                  <PasswordLabel htmlFor="signup-password">
                    <RequiredLabel>Password</RequiredLabel>
                  </PasswordLabel>
                </PasswordHeader>
                <PasswordInputWrapper>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    id="signup-password"
                    placeholder="••••••••"
                    value={fields.password}
                    onChange={set('password')}
                    iconSize="13.333px"
                    autoComplete="new-password"
                  />
                  <PasswordVisibilityToggle
                    visible={showPassword}
                    onToggle={() => setShowPassword((v) => !v)}
                  />
                </PasswordInputWrapper>
                {errors.password && <HelperText>{errors.password}</HelperText>}
              </div>

              <div style={{ minWidth: 0 }}>
                <PasswordHeader>
                  <PasswordLabel htmlFor="signup-confirm-password">
                    <RequiredLabel>Confirm Password</RequiredLabel>
                  </PasswordLabel>
                </PasswordHeader>
                <PasswordInputWrapper>
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    id="signup-confirm-password"
                    placeholder="••••••••"
                    value={fields.confirmPassword}
                    onChange={set('confirmPassword')}
                    iconSize="13.333px"
                    autoComplete="new-password"
                  />
                  <PasswordVisibilityToggle
                    visible={showConfirmPassword}
                    onToggle={() => setShowConfirmPassword((v) => !v)}
                  />
                </PasswordInputWrapper>
                {(showMismatch || errors.confirmPassword) && (
                  <HelperText>
                    {showMismatch ? 'Passwords do not match.' : errors.confirmPassword}
                  </HelperText>
                )}
              </div>
            </FieldRow>

            {/* Password policy: one wrapping row, shown while typing */}
            {fields.password.length > 0 && (
              <PolicyChips aria-live="polite">
                {policyResults.map((rule) => (
                  <PolicyChip key={rule.id} $passed={rule.passed}>
                    {rule.passed ? <Check size={12} /> : <Circle size={12} />}
                    <span>{rule.label}</span>
                  </PolicyChip>
                ))}
              </PolicyChips>
            )}

            <SignInButton type="submit" disabled={isLoading || !canSubmit}>
              {isLoading ? 'Creating account…' : 'Sign Up'}
            </SignInButton>
          </SignUpForm>

          <SignUpFooterText>
            Already have an account?{' '}
            <span
              role="button"
              tabIndex={0}
              onClick={onGoSignIn}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onGoSignIn()}
              style={{ cursor: 'pointer' }}
            >
              Sign In
            </span>
          </SignUpFooterText>
        </SignUpCard>
      </SignUpContainer>
    </PageWrapper>
  );
};

// ─── Email Verification ──────────────────────────────────────────────────────

const EmailVerification = ({ email, onVerificationSuccess }) => {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      alert('Please enter the verification code.');
      return;
    }
    try {
      setIsLoading(true);
      await confirmUser(email, code);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onVerificationSuccess();
      }, 2500);
    } catch {
      alert('Verification failed. Please check your code and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setIsResending(true);
      await resendVerificationCode(email);
      alert('Verification code sent! Please check your email.');
    } catch {
      alert('Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <PageWrapper>
      <LoginContainer>
        <LoginCard>
          <BrandAnchor>
            <Heading>{success ? '✓ Verified!' : 'Verify Email'}</Heading>
          </BrandAnchor>

          {!success && (
            <FormSection onSubmit={handleVerify}>
              <div style={{ width: '100%' }}>
                <Input
                  label="Email Address"
                  type="email"
                  value={email}
                  disabled
                  iconSize="16.667px"
                />
              </div>

              <div style={{ width: '100%' }}>
                <Input
                  label="Verification Code"
                  type="text"
                  name="verificationCode"
                  placeholder="Enter the code from your email"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  iconSize="16.667px"
                />
              </div>

              <SignInButton type="submit" disabled={isLoading}>
                {isLoading ? 'Verifying…' : 'Verify'}
              </SignInButton>
            </FormSection>
          )}

          {!success && (
            <SignUpText>
              Didn&apos;t receive a code?{' '}
              <span
                role="button"
                tabIndex={0}
                onClick={!isResending ? handleResend : undefined}
                onKeyDown={(e) =>
                  !isResending && (e.key === 'Enter' || e.key === ' ') && handleResend()
                }
                style={{
                  cursor: isResending ? 'default' : 'pointer',
                  opacity: isResending ? 0.6 : 1,
                }}
              >
                {isResending ? 'Sending…' : 'Resend Code'}
              </span>
            </SignUpText>
          )}

          {success && (
            <p style={{ textAlign: 'center', color: '#38a169', marginTop: '16px' }}>
              Email verified successfully! Redirecting to sign in…
            </p>
          )}
        </LoginCard>
      </LoginContainer>
    </PageWrapper>
  );
};

// ─── Forgot / Reset Password ─────────────────────────────────────────────────

const ForgotPassword = ({ onGoSignIn }) => {
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false); // false = "get code" step, true = "reset" step

  const policyResults = checkPasswordPolicy(newPassword);
  const policyMet = isPasswordPolicyMet(newPassword);
  const passwordsMatch = newPassword === confirmPassword;
  const showMismatch = confirmPassword.length > 0 && !passwordsMatch;
  const canSubmitReset =
    policyMet && confirmPassword !== '' && passwordsMatch && confirmationCode.trim() !== '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!codeSent) {
      // Step 1 — request reset code
      if (!validateEmail(email)) {
        setErrors({ email: 'Please enter a valid email address.' });
        setIsLoading(false);
        return;
      }
      try {
        await forgotPassword(email);
        setCodeSent(true);
      } catch (error) {
        alert(error?.message);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Step 2 — confirm new password
      const next = {};
      if (!newPassword) next.password = 'Password is required.';
      else if (!policyMet) next.password = 'Password does not meet the policy.';
      if (!confirmPassword) next.confirmPassword = 'Confirm password is required.';
      else if (!passwordsMatch) next.confirmPassword = 'Passwords do not match.';
      if (!confirmationCode.trim()) next.confirmationCode = 'Please enter the confirmation code.';
      if (Object.keys(next).length) {
        setErrors(next);
        setIsLoading(false);
        return;
      }
      try {
        await resetPassword(email, newPassword, confirmationCode);
        alert('Password reset successful. Please sign in.');
        onGoSignIn();
      } catch {
        alert('Reset failed. Please check your code and try again.');
        setCodeSent(false);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <PageWrapper>
      <LoginContainer>
        <ForgotPasswordCard>
          <BrandAnchor>
            <Heading>Forgot Password</Heading>
          </BrandAnchor>

          <FormSection onSubmit={handleSubmit}>
            <div style={{ width: '100%' }}>
              <Input
                label="Email Address"
                type="email"
                name="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors((p) => ({ ...p, email: '' }));
                }}
                disabled={codeSent}
                iconSize="16.667px"
              />
              {errors.email && <HelperText>{errors.email}</HelperText>}
            </div>

            {codeSent && (
              <>
                <FieldRow>
                  <div style={{ minWidth: 0 }}>
                    <PasswordHeader>
                      <PasswordLabel htmlFor="new-password">
                        <RequiredLabel>New Password</RequiredLabel>
                      </PasswordLabel>
                    </PasswordHeader>
                    <PasswordInputWrapper>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        id="new-password"
                        name="password"
                        placeholder="••••••••"
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          setErrors((p) => ({ ...p, password: '' }));
                        }}
                        iconSize="13.333px"
                        autoComplete="new-password"
                      />
                      <PasswordVisibilityToggle
                        visible={showPassword}
                        onToggle={() => setShowPassword((v) => !v)}
                      />
                    </PasswordInputWrapper>
                    {errors.password && <HelperText>{errors.password}</HelperText>}
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <PasswordHeader>
                      <PasswordLabel htmlFor="confirm-new-password">
                        <RequiredLabel>Confirm Password</RequiredLabel>
                      </PasswordLabel>
                    </PasswordHeader>
                    <PasswordInputWrapper>
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirm-new-password"
                        name="confirmPassword"
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setErrors((p) => ({ ...p, confirmPassword: '' }));
                        }}
                        iconSize="13.333px"
                        autoComplete="new-password"
                      />
                      <PasswordVisibilityToggle
                        visible={showConfirmPassword}
                        onToggle={() => setShowConfirmPassword((v) => !v)}
                      />
                    </PasswordInputWrapper>
                    {(showMismatch || errors.confirmPassword) && (
                      <HelperText>
                        {showMismatch ? 'Passwords do not match.' : errors.confirmPassword}
                      </HelperText>
                    )}
                  </div>
                </FieldRow>

                {newPassword.length > 0 && (
                  <PolicyChips aria-live="polite">
                    {policyResults.map((rule) => (
                      <PolicyChip key={rule.id} $passed={rule.passed}>
                        {rule.passed ? <Check size={12} /> : <Circle size={12} />}
                        <span>{rule.label}</span>
                      </PolicyChip>
                    ))}
                  </PolicyChips>
                )}

                <div style={{ width: '100%' }}>
                  <Input
                    label={<RequiredLabel>Confirmation Code</RequiredLabel>}
                    type="text"
                    name="confirmationCode"
                    placeholder="Enter code from your email"
                    value={confirmationCode}
                    onChange={(e) => {
                      setConfirmationCode(e.target.value);
                      setErrors((p) => ({ ...p, confirmationCode: '' }));
                    }}
                    iconSize="16.667px"
                  />
                  {errors.confirmationCode && <HelperText>{errors.confirmationCode}</HelperText>}
                </div>
              </>
            )}

            <SignInButton type="submit" disabled={isLoading || (codeSent && !canSubmitReset)}>
              {isLoading ? 'Please wait…' : codeSent ? 'Reset Password' : 'Get Code'}
            </SignInButton>
          </FormSection>

          <SignUpText>
            <span
              role="button"
              tabIndex={0}
              onClick={onGoSignIn}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onGoSignIn()}
              style={{ cursor: 'pointer' }}
            >
              ← Back to Sign In
            </span>
          </SignUpText>
        </ForgotPasswordCard>
      </LoginContainer>
    </PageWrapper>
  );
};
